import searchSoundboards from "@podcastsoundboard/ps-lib/api/soundboards/search"
import SoundboardCard from "@podcastsoundboard/ps-lib/components/SoundboardCard"
import copySoundboard from "@podcastsoundboard/ps-lib/api/soundboards/copy"
import { SoundboardCardProps } from "@podcastsoundboard/ps-lib/components/SoundboardCard/SoundboardCard"
import * as React from "react"
import { DndProvider, useDrag, useDrop } from "react-dnd"
import { HTML5Backend } from "react-dnd-html5-backend"
import { useNavigate } from "react-router-dom"
import updateSoundboardOrder from "../../../../api/soundboards/updateOrder"
import EditSoundboardModal from "../../../../components/EditSoundboardModal"
import HeadingContainer from "../../../../components/HeadingContainer"
import NewSoundboardModal from "../../../../components/NewSoundboardModal"
import { useAppDispatch } from "../../../../redux"
import { addSnackbar } from "../../../../redux/snackbars"
import { Soundboard } from "../../../../types/Soundboard"
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  Grid,
  Stack,
  Typography,
} from "@podcastsoundboard/ps-lib/ui"
import DragIndicatorIcon from "@podcastsoundboard/ps-lib/ui/icons/DragIndicator"

const DraggableContainer = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof Box>
>((props, ref) => <Box {...props} ref={ref} className="relative" />)
DraggableContainer.displayName = "DraggableContainer"

const DragIcon = (props: React.ComponentProps<typeof DragIndicatorIcon>) => (
  <DragIndicatorIcon
    {...props}
    className="absolute right-2 top-2 z-[1] cursor-grab rounded-full bg-paper p-1 text-text-secondary"
  />
)

const DraggableSoundboardCard = ({
  index,
  moveSoundboard,
  onDragEnd,
  ...soundboardCardProps
}: {
  index: number
  moveSoundboard: (dragIndex: number, hoverIndex: number) => void
  onDragEnd: () => void
  frontendWebSoundboard: Soundboard
} & SoundboardCardProps) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [duplicating, setDuplicating] = React.useState(false)
  const { frontendWebSoundboard } = soundboardCardProps
  const ref = React.useRef(null)
  const dragIconRef = React.useRef<HTMLDivElement>(null)

  const [, drop] = useDrop({
    accept: "SOUNDBOARD",
    hover(item: { index: number }) {
      if (!ref.current) {
        return
      }
      const dragIndex = item.index
      const hoverIndex = index
      if (dragIndex === hoverIndex) {
        return
      }
      moveSoundboard(dragIndex, hoverIndex)
      item.index = hoverIndex
    },
  })

  const [{ isDragging }, drag] = useDrag({
    type: "SOUNDBOARD",
    item: () => ({ id: frontendWebSoundboard.uuid, index }),
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    end: () => onDragEnd(),
  })

  const handleDuplicate = React.useCallback(
    async (soundboardUuid: string) => {
      if (!soundboardUuid) return
      try {
        setDuplicating(true)
        const newSoundboard = await copySoundboard({
          soundboardUuid,
        })
        setDuplicating(false)
        dispatch(addSnackbar({ text: "New soundboard created" }))
        navigate(`/app/account/soundboards/${newSoundboard.uuid}`)
      } catch (err) {
        setDuplicating(false)
        console.error("Error duplicating soundboard", err)
        dispatch(addSnackbar({ text: "Error duplicating soundboard" }))
      }
    },
    [dispatch, navigate],
  )

  drag(dragIconRef)
  drop(ref)

  return (
    <DraggableContainer
      ref={ref}
      style={{ opacity: isDragging ? 0.5 : 1, height: "100%" }}
    >
      <div className="dragIcon" ref={dragIconRef}>
        <DragIcon className="dragIcon" />
      </div>

      <SoundboardCard
        {...soundboardCardProps}
        duplicating={duplicating}
        onDuplicate={handleDuplicate}
      />
    </DraggableContainer>
  )
}

export default function SoundboardManagement() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [editSoundboardModalShown, setEditSoundboardModalShown] =
    React.useState(false)
  const [soundboardToEdit, setSoundboardToEdit] =
    React.useState<Soundboard | null>(null)
  const [soundboardsLoading, setSoundboardsLoading] = React.useState(true)
  const [soundboards, setSoundboards] = React.useState<Soundboard[] | null>(
    null,
  )
  const [newSoundboardModalShown, setNewSoundboardModalShown] =
    React.useState(false)
  const [errorMessage, setErrorMessage] = React.useState("")

  const hasOrderChangedRef = React.useRef(false)
  const soundboardsRef = React.useRef<Soundboard[] | null>(null)

  const handleSearchSoundboards = React.useCallback(() => {
    setSoundboardsLoading(true)
    searchSoundboards({ mine: true })
      .then(({ soundboards }) => {
        setSoundboards(soundboards)
        setSoundboardsLoading(false)
        hasOrderChangedRef.current = false
      })
      .catch((err) => {
        console.error(err)
        setSoundboardsLoading(false)
        setErrorMessage(
          (err.response?.data as { error?: string } | undefined)?.error ||
            err.message,
        )
      })
  }, [])

  React.useEffect(() => {
    handleSearchSoundboards()
  }, [handleSearchSoundboards])

  React.useEffect(() => {
    soundboardsRef.current = soundboards
  }, [soundboards])

  const moveSoundboard = React.useCallback(
    (dragIndex: number, hoverIndex: number) => {
      setSoundboards((prevSoundboards) => {
        if (!prevSoundboards) return null
        const newSoundboards = [...prevSoundboards]
        const [reorderedItem] = newSoundboards.splice(dragIndex, 1)
        newSoundboards.splice(hoverIndex, 0, reorderedItem)
        soundboardsRef.current = newSoundboards
        return newSoundboards
      })
      hasOrderChangedRef.current = true
    },
    [],
  )

  // Called from useDrag's `end` so the order is only saved on drop.
  const handleOrderUpdate = React.useCallback(async () => {
    if (!hasOrderChangedRef.current) return
    const currentSoundboards = soundboardsRef.current
    if (!currentSoundboards) return

    const order = currentSoundboards.map((sb) => sb.uuid)
    try {
      await updateSoundboardOrder(order)
      dispatch(addSnackbar({ text: "Soundboard order updated successfully." }))
      hasOrderChangedRef.current = false
    } catch (error) {
      console.error("Error updating soundboard order:", error)
      dispatch(addSnackbar({ text: "Failed to update soundboard order." }))
    }
  }, [dispatch])

  return (
    <>
      {errorMessage && (
        <Stack className="w-full" spacing={2}>
          <Alert className="rounded-none" severity="error">
            {errorMessage}
          </Alert>
        </Stack>
      )}

      <NewSoundboardModal
        open={newSoundboardModalShown}
        handleClose={() => setNewSoundboardModalShown(false)}
        onCreate={() => {
          setNewSoundboardModalShown(false)
          handleSearchSoundboards()
        }}
      />

      {soundboardToEdit && (
        <EditSoundboardModal
          open={editSoundboardModalShown}
          soundboard={soundboardToEdit}
          handleClose={() => {
            setEditSoundboardModalShown(false)
            setSoundboardToEdit(null)
          }}
          onUpdate={() => {
            setEditSoundboardModalShown(false)
            setSoundboardToEdit(null)
            handleSearchSoundboards()
          }}
        />
      )}
      <DndProvider backend={HTML5Backend}>
        <HeadingContainer className="mb-8">
          <Typography component="h1" variant="h6" color="textPrimary" noWrap>
            Soundboards
          </Typography>
        </HeadingContainer>

        {soundboardsLoading && (
          <Box className="flex justify-center">
            <CircularProgress />
          </Box>
        )}

        {!soundboardsLoading && (
          <Container maxWidth="md">
            <Grid container spacing={2}>
              {soundboards &&
                soundboards.map((soundboard, index) => (
                  <Grid item xs={12} key={soundboard.uuid}>
                    <DraggableSoundboardCard
                      frontendWebSoundboard={soundboard}
                      index={index}
                      moveSoundboard={moveSoundboard}
                      onDragEnd={handleOrderUpdate}
                      onSetSoundboardToEdit={() => {
                        setSoundboardToEdit(soundboard)
                        setEditSoundboardModalShown(true)
                      }}
                      onOpenSoundboard={() => {
                        navigate(`/app/account/soundboards/${soundboard.uuid}`)
                      }}
                    />
                  </Grid>
                ))}
            </Grid>
          </Container>
        )}
      </DndProvider>
    </>
  )
}
